/**
 * Dinamik Rapor Motoru — Frontend Kataloğu
 *
 * Kaynak/metrik/boyut/filtre id'leri backend'deki ReportCatalog.cs ile birebir aynıdır.
 * Backend bu id'leri whitelist olarak doğrular; burada tanımlı olmayan bir kombinasyon
 * gönderilirse 400 döner. Yeni kaynak eklemek = backend'e sınıf + buraya kayıt.
 */

export type ReportVisual = 'kpi' | 'bar' | 'pie' | 'line' | 'table';
export type ReportTimeGranularity = 'hour' | 'day' | 'weekday' | 'month';
export type ReportMetricFormat = 'currency' | 'count' | 'minutes';

export interface ReportSpecFilter {
  field: string;
  values: string[];
}

/** Kullanıcının kurduğu rapor kartının tam tarifi (localStorage'da saklanır) */
export interface ReportSpec {
  id: string; // "custom_" + uuid
  title: string;
  dataSource: string;
  metric: string;
  dimension: string; // "none" = tek KPI
  timeGranularity?: ReportTimeGranularity;
  filters: ReportSpecFilter[];
  visual: ReportVisual;
  limit: number;
}

export interface ReportMetricDef {
  id: string;
  labelKey: string;
  format: ReportMetricFormat;
}

export interface ReportDimensionDef {
  id: string;
  labelKey: string;
}

export interface ReportFilterOption {
  value: string;
  /** i18n anahtarı; yoksa value ham gösterilir */
  labelKey?: string;
}

export interface ReportFilterDef {
  id: string;
  labelKey: string;
  /**
   * branch: şube çoktan seçmeli, screen: hızlı satış ekranı listesi,
   * restaurantScreen: restoran ekranı listesi, cashbox: kasa listesi,
   * bankAccount: banka hesabı listesi, warehouse: depo listesi, options: sabit değerler
   */
  type: 'branch' | 'screen' | 'restaurantScreen' | 'cashbox' | 'bankAccount' | 'warehouse' | 'options';
  options?: ReportFilterOption[];
}

export interface ReportSourceDef {
  id: string;
  labelKey: string;
  descriptionKey: string;
  metrics: ReportMetricDef[];
  dimensions: ReportDimensionDef[];
  filters: ReportFilterDef[];
}

const V = 'dashboard.reportBuilder.values';
const C = 'dashboard.reportBuilder.catalog';

const ORDER_SOURCE_OPTIONS: ReportFilterOption[] = [
  { value: 'quicksale', labelKey: `${V}.orderSource.quicksale` },
  { value: 'package', labelKey: `${V}.orderSource.package` },
  { value: 'getir', labelKey: `${V}.orderSource.getir` },
  { value: 'prepaid', labelKey: `${V}.orderSource.prepaid` },
];

const PAYMENT_TYPE_OPTIONS: ReportFilterOption[] = [
  { value: 'CASH', labelKey: `${V}.paymentType.CASH` },
  { value: 'POS', labelKey: `${V}.paymentType.POS` },
  { value: 'PARTIAL', labelKey: `${V}.paymentType.PARTIAL` },
  { value: 'PREPAID', labelKey: `${V}.paymentType.PREPAID` },
];

const PAYMENT_METHOD_OPTIONS: ReportFilterOption[] = [
  { value: 'cash', labelKey: `${V}.paymentMethod.cash` },
  { value: 'pos', labelKey: `${V}.paymentMethod.pos` },
  { value: 'meal_card', labelKey: `${V}.paymentMethod.meal_card` },
  { value: 'gift_card', labelKey: `${V}.paymentMethod.gift_card` },
  { value: 'open_account', labelKey: `${V}.paymentMethod.open_account` },
];

const PROVIDER_OPTIONS: ReportFilterOption[] = [
  { value: 'SODEXO' },
  { value: 'MULTINET' },
  { value: 'TICKET' },
  { value: 'SETCARD' },
  { value: 'GIFT_CARD', labelKey: `${V}.provider.GIFT_CARD` },
];

const ORDER_STATUS_OPTIONS: ReportFilterOption[] = [
  { value: 'PENDING', labelKey: `${V}.orderStatus.PENDING` },
  { value: 'CONFIRMED', labelKey: `${V}.orderStatus.CONFIRMED` },
  { value: 'PREPARING', labelKey: `${V}.orderStatus.PREPARING` },
  { value: 'READY', labelKey: `${V}.orderStatus.READY` },
  { value: 'ON_DELIVERY', labelKey: `${V}.orderStatus.ON_DELIVERY` },
  { value: 'DELIVERED', labelKey: `${V}.orderStatus.DELIVERED` },
  { value: 'CANCELLED', labelKey: `${V}.orderStatus.CANCELLED` },
];

const CHANNEL_OPTIONS: ReportFilterOption[] = [
  { value: 'phone', labelKey: `${V}.channel.phone` },
  { value: 'getir', labelKey: `${V}.channel.getir` },
  { value: 'trendyol', labelKey: `${V}.channel.trendyol` },
  { value: 'yemeksepeti', labelKey: `${V}.channel.yemeksepeti` },
  { value: 'migros', labelKey: `${V}.channel.migros` },
];

const CASH_TYPE_OPTIONS: ReportFilterOption[] = [
  { value: 'INCOME', labelKey: `${V}.cashType.INCOME` },
  { value: 'EXPENSE', labelKey: `${V}.cashType.EXPENSE` },
];

const ACCOUNT_TX_TYPE_OPTIONS: ReportFilterOption[] = [
  { value: 'SALE_WITH_STOCK', labelKey: `${V}.accountTxType.SALE_WITH_STOCK` },
  { value: 'SALE_WITHOUT_STOCK', labelKey: `${V}.accountTxType.SALE_WITHOUT_STOCK` },
  { value: 'COLLECTION', labelKey: `${V}.accountTxType.COLLECTION` },
  { value: 'PAYMENT', labelKey: `${V}.accountTxType.PAYMENT` },
  { value: 'CREDIT', labelKey: `${V}.accountTxType.CREDIT` },
  { value: 'DEBIT', labelKey: `${V}.accountTxType.DEBIT` },
  { value: 'INVOICE', labelKey: `${V}.accountTxType.INVOICE` },
  { value: 'QUICK_SALE', labelKey: `${V}.accountTxType.QUICK_SALE` },
  { value: 'OFFSETTING', labelKey: `${V}.accountTxType.OFFSETTING` },
];

const INVOICE_TYPE_OPTIONS: ReportFilterOption[] = [
  { value: 'SALES', labelKey: `${V}.invoiceType.SALES` },
  { value: 'PURCHASE', labelKey: `${V}.invoiceType.PURCHASE` },
  { value: 'QUICK_SALE', labelKey: `${V}.invoiceType.QUICK_SALE` },
];

const INVOICE_STATUS_OPTIONS: ReportFilterOption[] = [
  { value: 'draft', labelKey: `${V}.invoiceStatus.draft` },
  { value: 'completed', labelKey: `${V}.invoiceStatus.completed` },
  { value: 'canceled', labelKey: `${V}.invoiceStatus.canceled` },
];

const MOVEMENT_TYPE_OPTIONS: ReportFilterOption[] = [
  { value: 'QUICK_SALE', labelKey: `${V}.movementType.QUICK_SALE` },
  { value: 'PURCHASE', labelKey: `${V}.movementType.PURCHASE` },
  { value: 'TRANSFER_IN', labelKey: `${V}.movementType.TRANSFER_IN` },
  { value: 'TRANSFER_OUT', labelKey: `${V}.movementType.TRANSFER_OUT` },
  { value: 'ADJUSTMENT', labelKey: `${V}.movementType.ADJUSTMENT` },
  { value: 'RETURN', labelKey: `${V}.movementType.RETURN` },
  { value: 'PRODUCTION', labelKey: `${V}.movementType.PRODUCTION` },
  { value: 'CONSUMPTION', labelKey: `${V}.movementType.CONSUMPTION` },
];

const DIRECTION_OPTIONS: ReportFilterOption[] = [
  { value: 'IN', labelKey: `${V}.direction.IN` },
  { value: 'OUT', labelKey: `${V}.direction.OUT` },
];

const CHECK_STATUS_OPTIONS: ReportFilterOption[] = [
  { value: 'PORTFOLIO', labelKey: `${V}.checkStatus.PORTFOLIO` },
  { value: 'IN_COLLECTION', labelKey: `${V}.checkStatus.IN_COLLECTION` },
  { value: 'COLLECTED', labelKey: `${V}.checkStatus.COLLECTED` },
  { value: 'PARTIAL_COLLECTED', labelKey: `${V}.checkStatus.PARTIAL_COLLECTED` },
  { value: 'ENDORSED', labelKey: `${V}.checkStatus.ENDORSED` },
  { value: 'COLLATERAL', labelKey: `${V}.checkStatus.COLLATERAL` },
  { value: 'BOUNCED', labelKey: `${V}.checkStatus.BOUNCED` },
  { value: 'RETURNED', labelKey: `${V}.checkStatus.RETURNED` },
  { value: 'CANCELED', labelKey: `${V}.checkStatus.CANCELED` },
  { value: 'GIVEN_PENDING', labelKey: `${V}.checkStatus.GIVEN_PENDING` },
  { value: 'GIVEN_PAID', labelKey: `${V}.checkStatus.GIVEN_PAID` },
  { value: 'GIVEN_RETURNED', labelKey: `${V}.checkStatus.GIVEN_RETURNED` },
];

const CHECK_DIRECTION_OPTIONS: ReportFilterOption[] = [
  { value: 'RECEIVED', labelKey: `${V}.checkDirection.RECEIVED` },
  { value: 'GIVEN', labelKey: `${V}.checkDirection.GIVEN` },
];

const CHECK_TYPE_OPTIONS: ReportFilterOption[] = [
  { value: 'CHECK', labelKey: `${V}.checkType.CHECK` },
  { value: 'PROMISSORY_NOTE', labelKey: `${V}.checkType.PROMISSORY_NOTE` },
];

export const REPORT_SOURCES: ReportSourceDef[] = [
  {
    id: 'quickSaleReceipts',
    labelKey: `${C}.quickSaleReceipts.label`,
    descriptionKey: `${C}.quickSaleReceipts.description`,
    metrics: [
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'receiptCount', labelKey: `${C}.metrics.receiptCount`, format: 'count' },
      { id: 'avgBasket', labelKey: `${C}.metrics.avgBasket`, format: 'currency' },
      { id: 'totalDiscount', labelKey: `${C}.metrics.totalDiscount`, format: 'currency' },
    ],
    dimensions: [
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'screen', labelKey: `${C}.dimensions.screen` },
      { id: 'cashier', labelKey: `${C}.dimensions.cashier` },
      { id: 'paymentType', labelKey: `${C}.dimensions.paymentType` },
      { id: 'orderSource', labelKey: `${C}.dimensions.orderSource` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
      { id: 'screen', labelKey: `${C}.dimensions.screen`, type: 'screen' },
      { id: 'orderSource', labelKey: `${C}.dimensions.orderSource`, type: 'options', options: ORDER_SOURCE_OPTIONS },
      { id: 'paymentType', labelKey: `${C}.dimensions.paymentType`, type: 'options', options: PAYMENT_TYPE_OPTIONS },
    ],
  },
  {
    id: 'receiptPayments',
    labelKey: `${C}.receiptPayments.label`,
    descriptionKey: `${C}.receiptPayments.description`,
    metrics: [
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'paymentCount', labelKey: `${C}.metrics.paymentCount`, format: 'count' },
    ],
    dimensions: [
      { id: 'paymentMethod', labelKey: `${C}.dimensions.paymentMethod` },
      { id: 'provider', labelKey: `${C}.dimensions.provider` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
      { id: 'paymentMethod', labelKey: `${C}.dimensions.paymentMethod`, type: 'options', options: PAYMENT_METHOD_OPTIONS },
      { id: 'provider', labelKey: `${C}.dimensions.provider`, type: 'options', options: PROVIDER_OPTIONS },
    ],
  },
  {
    id: 'quickSaleItems',
    labelKey: `${C}.quickSaleItems.label`,
    descriptionKey: `${C}.quickSaleItems.description`,
    metrics: [
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'totalQuantity', labelKey: `${C}.metrics.totalQuantity`, format: 'count' },
    ],
    dimensions: [
      { id: 'product', labelKey: `${C}.dimensions.product` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
      { id: 'screen', labelKey: `${C}.dimensions.screen`, type: 'screen' },
    ],
  },
  {
    id: 'restaurantSales',
    labelKey: `${C}.restaurantSales.label`,
    descriptionKey: `${C}.restaurantSales.description`,
    metrics: [
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'saleCount', labelKey: `${C}.metrics.saleCount`, format: 'count' },
      { id: 'avgSale', labelKey: `${C}.metrics.avgSale`, format: 'currency' },
      { id: 'avgDurationMinutes', labelKey: `${C}.metrics.avgDurationMinutes`, format: 'minutes' },
    ],
    dimensions: [
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'waiter', labelKey: `${C}.dimensions.waiter` },
      { id: 'tableGroup', labelKey: `${C}.dimensions.tableGroup` },
      { id: 'paymentMethod', labelKey: `${C}.dimensions.paymentMethod` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
      { id: 'restaurantScreen', labelKey: `${C}.dimensions.restaurantScreen`, type: 'restaurantScreen' },
    ],
  },
  {
    id: 'restaurantItems',
    labelKey: `${C}.restaurantItems.label`,
    descriptionKey: `${C}.restaurantItems.description`,
    metrics: [
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'totalQuantity', labelKey: `${C}.metrics.totalQuantity`, format: 'count' },
    ],
    dimensions: [
      { id: 'product', labelKey: `${C}.dimensions.product` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [{ id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' }],
  },
  {
    id: 'packageOrders',
    labelKey: `${C}.packageOrders.label`,
    descriptionKey: `${C}.packageOrders.description`,
    metrics: [
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'orderCount', labelKey: `${C}.metrics.orderCount`, format: 'count' },
      { id: 'avgBasket', labelKey: `${C}.metrics.avgBasket`, format: 'currency' },
      { id: 'avgDeliveryMinutes', labelKey: `${C}.metrics.avgDeliveryMinutes`, format: 'minutes' },
      { id: 'cancelledCount', labelKey: `${C}.metrics.cancelledCount`, format: 'count' },
    ],
    dimensions: [
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'courier', labelKey: `${C}.dimensions.courier` },
      { id: 'orderStatus', labelKey: `${C}.dimensions.orderStatus` },
      { id: 'channel', labelKey: `${C}.dimensions.channel` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
      { id: 'orderStatus', labelKey: `${C}.dimensions.orderStatus`, type: 'options', options: ORDER_STATUS_OPTIONS },
      { id: 'channel', labelKey: `${C}.dimensions.channel`, type: 'options', options: CHANNEL_OPTIONS },
    ],
  },
  {
    id: 'packageOrderItems',
    labelKey: `${C}.packageOrderItems.label`,
    descriptionKey: `${C}.packageOrderItems.description`,
    metrics: [
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'totalQuantity', labelKey: `${C}.metrics.totalQuantity`, format: 'count' },
    ],
    dimensions: [
      { id: 'product', labelKey: `${C}.dimensions.product` },
      { id: 'category', labelKey: `${C}.dimensions.category` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [{ id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' }],
  },
  {
    id: 'cashboxTransactions',
    labelKey: `${C}.cashboxTransactions.label`,
    descriptionKey: `${C}.cashboxTransactions.description`,
    metrics: [
      { id: 'incomeTotal', labelKey: `${C}.metrics.incomeTotal`, format: 'currency' },
      { id: 'expenseTotal', labelKey: `${C}.metrics.expenseTotal`, format: 'currency' },
      { id: 'netTotal', labelKey: `${C}.metrics.netTotal`, format: 'currency' },
      { id: 'txCount', labelKey: `${C}.metrics.txCount`, format: 'count' },
    ],
    dimensions: [
      { id: 'cashbox', labelKey: `${C}.dimensions.cashbox` },
      { id: 'type', labelKey: `${C}.dimensions.type` },
      { id: 'transactionType', labelKey: `${C}.dimensions.transactionType` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'cashbox', labelKey: `${C}.dimensions.cashbox`, type: 'cashbox' },
      { id: 'type', labelKey: `${C}.dimensions.type`, type: 'options', options: CASH_TYPE_OPTIONS },
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
    ],
  },
  {
    // Banka hareketlerinde şube bilgisi yoktur — şube boyutu/filtresi sunulmaz
    id: 'bankTransactions',
    labelKey: `${C}.bankTransactions.label`,
    descriptionKey: `${C}.bankTransactions.description`,
    metrics: [
      { id: 'incomeTotal', labelKey: `${C}.metrics.incomeTotal`, format: 'currency' },
      { id: 'expenseTotal', labelKey: `${C}.metrics.expenseTotal`, format: 'currency' },
      { id: 'netTotal', labelKey: `${C}.metrics.netTotal`, format: 'currency' },
      { id: 'txCount', labelKey: `${C}.metrics.txCount`, format: 'count' },
    ],
    dimensions: [
      { id: 'account', labelKey: `${C}.dimensions.bankAccount` },
      { id: 'type', labelKey: `${C}.dimensions.type` },
      { id: 'transactionType', labelKey: `${C}.dimensions.transactionType` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'account', labelKey: `${C}.dimensions.bankAccount`, type: 'bankAccount' },
      { id: 'type', labelKey: `${C}.dimensions.type`, type: 'options', options: CASH_TYPE_OPTIONS },
    ],
  },
  {
    id: 'accountTransactions',
    labelKey: `${C}.accountTransactions.label`,
    descriptionKey: `${C}.accountTransactions.description`,
    metrics: [
      { id: 'debitTotal', labelKey: `${C}.metrics.debitTotal`, format: 'currency' },
      { id: 'creditTotal', labelKey: `${C}.metrics.creditTotal`, format: 'currency' },
      { id: 'netTotal', labelKey: `${C}.metrics.netTotal`, format: 'currency' },
      { id: 'txCount', labelKey: `${C}.metrics.txCount`, format: 'count' },
    ],
    dimensions: [
      { id: 'account', labelKey: `${C}.dimensions.account` },
      { id: 'transactionType', labelKey: `${C}.dimensions.transactionType` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      {
        id: 'transactionType',
        labelKey: `${C}.dimensions.transactionType`,
        type: 'options',
        options: ACCOUNT_TX_TYPE_OPTIONS,
      },
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
    ],
  },
  {
    id: 'invoices',
    labelKey: `${C}.invoices.label`,
    descriptionKey: `${C}.invoices.description`,
    metrics: [
      { id: 'grandTotal', labelKey: `${C}.metrics.grandTotal`, format: 'currency' },
      { id: 'invoiceCount', labelKey: `${C}.metrics.invoiceCount`, format: 'count' },
      { id: 'vatTotal', labelKey: `${C}.metrics.vatTotal`, format: 'currency' },
    ],
    dimensions: [
      { id: 'invoiceType', labelKey: `${C}.dimensions.invoiceType` },
      { id: 'status', labelKey: `${C}.dimensions.status` },
      { id: 'account', labelKey: `${C}.dimensions.account` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'invoiceType', labelKey: `${C}.dimensions.invoiceType`, type: 'options', options: INVOICE_TYPE_OPTIONS },
      { id: 'status', labelKey: `${C}.dimensions.status`, type: 'options', options: INVOICE_STATUS_OPTIONS },
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
    ],
  },
  {
    id: 'stockMovements',
    labelKey: `${C}.stockMovements.label`,
    descriptionKey: `${C}.stockMovements.description`,
    metrics: [
      { id: 'totalQuantity', labelKey: `${C}.metrics.totalQuantity`, format: 'count' },
      { id: 'totalAmount', labelKey: `${C}.metrics.totalAmount`, format: 'currency' },
      { id: 'movementCount', labelKey: `${C}.metrics.movementCount`, format: 'count' },
    ],
    dimensions: [
      { id: 'product', labelKey: `${C}.dimensions.product` },
      { id: 'warehouse', labelKey: `${C}.dimensions.warehouse` },
      { id: 'movementType', labelKey: `${C}.dimensions.movementType` },
      { id: 'direction', labelKey: `${C}.dimensions.direction` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'movementType', labelKey: `${C}.dimensions.movementType`, type: 'options', options: MOVEMENT_TYPE_OPTIONS },
      { id: 'direction', labelKey: `${C}.dimensions.direction`, type: 'options', options: DIRECTION_OPTIONS },
      { id: 'warehouse', labelKey: `${C}.dimensions.warehouse`, type: 'warehouse' },
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
    ],
  },
  {
    // Dashboard tarih aralığı bu kaynakta VADE tarihine (DueDate) uygulanır
    id: 'checkNotes',
    labelKey: `${C}.checkNotes.label`,
    descriptionKey: `${C}.checkNotes.description`,
    metrics: [
      { id: 'totalAmountTry', labelKey: `${C}.metrics.totalAmountTry`, format: 'currency' },
      { id: 'noteCount', labelKey: `${C}.metrics.noteCount`, format: 'count' },
      { id: 'collectedAmount', labelKey: `${C}.metrics.collectedAmount`, format: 'currency' },
    ],
    dimensions: [
      { id: 'status', labelKey: `${C}.dimensions.status` },
      { id: 'direction', labelKey: `${C}.dimensions.direction` },
      { id: 'type', labelKey: `${C}.dimensions.checkType` },
      { id: 'account', labelKey: `${C}.dimensions.account` },
      { id: 'branch', labelKey: `${C}.dimensions.branch` },
      { id: 'time', labelKey: `${C}.dimensions.time` },
    ],
    filters: [
      { id: 'status', labelKey: `${C}.dimensions.status`, type: 'options', options: CHECK_STATUS_OPTIONS },
      { id: 'direction', labelKey: `${C}.dimensions.direction`, type: 'options', options: CHECK_DIRECTION_OPTIONS },
      { id: 'type', labelKey: `${C}.dimensions.checkType`, type: 'options', options: CHECK_TYPE_OPTIONS },
      { id: 'branch', labelKey: `${C}.dimensions.branch`, type: 'branch' },
    ],
  },
];

export const getReportSource = (sourceId: string): ReportSourceDef | undefined =>
  REPORT_SOURCES.find((s) => s.id === sourceId);

export const getMetricDef = (sourceId: string, metricId: string): ReportMetricDef | undefined =>
  getReportSource(sourceId)?.metrics.find((m) => m.id === metricId);

/** Boyuta göre izinli görsel tipler (anlamsız kombinasyonlar sunulmaz) */
export const getAllowedVisuals = (dimension: string): ReportVisual[] => {
  if (dimension === 'none') return ['kpi'];
  if (dimension === 'time') return ['line', 'bar', 'table'];
  return ['bar', 'pie', 'table'];
};

/**
 * Boyut anahtarını görünen etikete çevirir (katalog options'ları üzerinden).
 * Bulunamayan anahtarlar ham döner; şube ve haftanın günü çözümü kart bileşeninde yapılır.
 */
export const getDimensionValueLabelKey = (
  sourceId: string,
  dimensionId: string,
  key: string
): string | undefined => {
  const source = getReportSource(sourceId);
  const filter = source?.filters.find((f) => f.id === dimensionId);
  return filter?.options?.find((o) => o.value === key)?.labelKey;
};

export const TIME_GRANULARITIES: Array<{ id: ReportTimeGranularity; labelKey: string }> = [
  { id: 'hour', labelKey: `${C}.granularity.hour` },
  { id: 'day', labelKey: `${C}.granularity.day` },
  { id: 'weekday', labelKey: `${C}.granularity.weekday` },
  { id: 'month', labelKey: `${C}.granularity.month` },
];

/**
 * Backend kataloğu (GET /api/DynamicReport/catalog) ile bu dosyayı karşılaştırır,
 * uyumsuzlukları console.warn ile bildirir (sürüm kayması tespiti).
 * Karşılaştırma yalnız id bazlıdır; etiketler frontend i18n'den gelir.
 */
export const verifyReportCatalogAgainstBackend = (backendSources: Array<{
  sourceId: string;
  metrics: string[];
  dimensions: string[];
  filters: string[];
}>): void => {
  const prefix = '[reportCatalog] Backend katalog uyumsuzluğu:';
  const backendById = new Map(backendSources.map((s) => [s.sourceId, s]));

  for (const source of REPORT_SOURCES) {
    const backend = backendById.get(source.id);
    if (!backend) {
      console.warn(`${prefix} '${source.id}' kaynağı backend'de tanımlı değil`);
      continue;
    }
    const feMetrics = source.metrics.map((m) => m.id);
    const missingMetrics = feMetrics.filter((m) => !backend.metrics.includes(m));
    const extraMetrics = backend.metrics.filter((m) => !feMetrics.includes(m));
    if (missingMetrics.length || extraMetrics.length) {
      console.warn(`${prefix} '${source.id}' metrikleri farklı`, { missingMetrics, extraMetrics });
    }
    // Backend "none" boyutunu da listeler; frontend'te ayrı seçenek olarak sunulur
    const feDimensions = source.dimensions.map((d) => d.id);
    const beDimensions = backend.dimensions.filter((d) => d !== 'none');
    const missingDims = feDimensions.filter((d) => !beDimensions.includes(d));
    const extraDims = beDimensions.filter((d) => !feDimensions.includes(d));
    if (missingDims.length || extraDims.length) {
      console.warn(`${prefix} '${source.id}' boyutları farklı`, { missingDims, extraDims });
    }
    const feFilters = source.filters.map((f) => f.id);
    const missingFilters = feFilters.filter((f) => !backend.filters.includes(f));
    const extraFilters = backend.filters.filter((f) => !feFilters.includes(f));
    if (missingFilters.length || extraFilters.length) {
      console.warn(`${prefix} '${source.id}' filtreleri farklı`, { missingFilters, extraFilters });
    }
  }

  for (const backend of backendSources) {
    if (!REPORT_SOURCES.some((s) => s.id === backend.sourceId)) {
      console.warn(`${prefix} backend '${backend.sourceId}' kaynağını sunuyor ama frontend katalogda yok`);
    }
  }
};

export const CUSTOM_REPORT_ID_PREFIX = 'custom_';

export const createReportId = (): string =>
  `${CUSTOM_REPORT_ID_PREFIX}${
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  }`;
